import "./landing.css";
import isMobile from "./helpers/mobile.js";
import { mountLandingWallpaper } from "./helpers/wallpaperCanvas.js";
import { drawFullImageOnCanvas, runPixelShuffleTransition } from "./helpers/onboardingImageTransition.js";
import { initLandingScrolls } from "./helpers/landingScroll.js";

const SLIDE_COUNT = 15;
const SLIDE_INTERVAL_MS = 5000;
const WINDOW_PADDING = 20.0;

function imageUrl(index) {
	return "/onboarding/america-os-" + index + ".jpeg";
}

function shuffledIndices() {
	const indices = [];

	for (let i = 1; i <= SLIDE_COUNT; i++) {
		indices.push(i);
	}

	for (let i = indices.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		const t = indices[i];
		indices[i] = indices[j];
		indices[j] = t;
	}

	return indices;
}

function initMobileSlideshow(container) {
	const imgs = container.querySelectorAll("img[data-onboarding-slide]");

	if (imgs.length === 0) {
		return;
	}

	const order = shuffledIndices();
	let position = 0;

	function showOnlyActive() {
		const activeIndex = order[position];

		for (let i = 0; i < imgs.length; i++) {
			const img = imgs[i];
			const n = Number(img.getAttribute("data-onboarding-slide"));

			if (n === activeIndex) {
				img.hidden = false;
			} else {
				img.hidden = true;
			}
		}
	}

	showOnlyActive();

	setInterval(function advance() {
		position = (position + 1) % order.length;
		showOnlyActive();
	}, SLIDE_INTERVAL_MS);
}

function initDesktopSlideshow(container) {
	const canvas = container.querySelector(".landing-slideshow-canvas");
	const fallback = container.querySelector(".landing-slideshow-fallback");

	if (!canvas || !fallback) {
		return;
	}

	fallback.hidden = true;
	canvas.hidden = false;
	container.classList.add("landing-slideshow-enhanced");

	const order = shuffledIndices();
	let position = 0;

	function advance() {
		setTimeout(async function onTick() {
			position = (position + 1) % order.length;

			await runPixelShuffleTransition(canvas, imageUrl(order[position]), function shouldAbort() {
				return false;
			});

			advance();
		}, SLIDE_INTERVAL_MS);
	}

	drawFullImageOnCanvas(canvas, imageUrl(order[position]))
		.catch(function () {})
		.then(function () {
			advance();
		});
}

function initSlideshow() {
	const container = document.querySelector(".landing-slideshow");

	if (!container) {
		return;
	}

	if (isMobile === true) {
		initMobileSlideshow(container);
		return;
	}

	initDesktopSlideshow(container);
}

function pointerLocation(e) {
	if (e.touches && e.touches.length > 0) {
		const [touch] = e.touches;

		return { x: touch.pageX, y: touch.pageY };
	}

	return { x: e.clientX, y: e.clientY };
}

function landingWindowKind(win) {
	const list = win.classList;

	for (let i = 0; i < list.length; i++) {
		const c = list[i];

		if (c.indexOf("landing-window--") === 0 && c !== "landing-window--scroll") {
			return c;
		}
	}

	return win.className;
}

function logLandingWindowSnapshot(win, reason) {
	const iw = globalThis.innerWidth;
	const ih = globalThis.innerHeight;
	const ww = win.offsetWidth;
	const wh = win.offsetHeight;
	const cx = win.offsetLeft + ww / 2;
	const cy = win.offsetTop + wh / 2;
	const fromCenterVw = ((cx - iw / 2) / iw) * 100;
	const fromCenterVh = ((cy - ih / 2) / ih) * 100;

	console.log("[landing-window]", reason, {
		kind: landingWindowKind(win),
		id: win.id || null,
		offsetLeft: win.offsetLeft,
		offsetTop: win.offsetTop,
		styleLeft: win.style.left,
		styleTop: win.style.top,
		width: ww,
		height: wh,
		centerFromViewportPercent: { vw: Number(fromCenterVw.toFixed(1)), vh: Number(fromCenterVh.toFixed(1)) },
		viewport: { innerWidth: iw, innerHeight: ih }
	});
}

function logAllLandingWindows(reason) {
	const wins = document.querySelectorAll(".landing-windows > .landing-window");

	if (wins.length === 0) {
		return;
	}

	const iw = globalThis.innerWidth;
	const ih = globalThis.innerHeight;
	const rows = [];

	for (let i = 0; i < wins.length; i++) {
		const win = wins[i];
		const ww = win.offsetWidth;
		const wh = win.offsetHeight;
		const cx = win.offsetLeft + ww / 2;
		const cy = win.offsetTop + wh / 2;
		const fromCenterVw = ((cx - iw / 2) / iw) * 100;
		const fromCenterVh = ((cy - ih / 2) / ih) * 100;

		rows.push({
			kind: landingWindowKind(win),
			id: win.id || null,
			offsetLeft: win.offsetLeft,
			offsetTop: win.offsetTop,
			styleLeft: win.style.left,
			styleTop: win.style.top,
			width: ww,
			height: wh,
			centerFromViewportPercent: { vw: Number(fromCenterVw.toFixed(1)), vh: Number(fromCenterVh.toFixed(1)) }
		});
	}

	console.log("[landing-windows]", reason, rows);
}

function bringLandingWindowToFront(win) {
	const stage = document.querySelector(".landing-windows");

	if (!stage || stage.contains(win) === false) {
		return;
	}

	const siblings = document.querySelectorAll(".landing-windows > .landing-window");
	let maxZ = 0;

	for (let i = 0; i < siblings.length; i++) {
		const z = parseInt(globalThis.getComputedStyle(siblings[i]).zIndex, 10);

		if (Number.isNaN(z) === false && z > maxZ) {
			maxZ = z;
		}
	}

	win.style.zIndex = String(maxZ + 1);
}

function initLandingWindowFocusOrder() {
	const stage = document.querySelector(".landing-windows");

	if (!stage) {
		return;
	}

	document.querySelectorAll(".landing-windows > .landing-window").forEach(function forEachWindow(win) {
		function didPointerDown() {
			bringLandingWindowToFront(win);
		}

		win.addEventListener("mousedown", didPointerDown, true);
		win.addEventListener("touchstart", didPointerDown, { capture: true, passive: true });
	});
}

function materializeWindowPositions() {
	const stage = document.querySelector(".landing-windows");

	if (!stage) {
		return;
	}

	const stageRect = stage.getBoundingClientRect();

	document.querySelectorAll(".landing-windows > .landing-window").forEach(function forEachWindow(win) {
		const winRect = win.getBoundingClientRect();
		const left = winRect.left - stageRect.left + stage.scrollLeft;
		const top = winRect.top - stageRect.top + stage.scrollTop;

		win.style.left = left + "px";
		win.style.top = top + "px";
		win.style.transform = "none";
	});

	logAllLandingWindows("materialize");
}

function applyScenesAspectFromPhoto(onApplied) {
	const slideshow = document.querySelector(".landing-window--scenes .landing-slideshow");
	const img = document.querySelector('.landing-window--scenes img[data-onboarding-slide="1"]');

	if (!slideshow || !img) {
		if (typeof onApplied === "function") {
			onApplied();
		}

		return;
	}

	function apply() {
		const w = img.naturalWidth;
		const h = img.naturalHeight;

		if (isMobile === true && w > 0 && h > 0) {
			slideshow.style.aspectRatio = w + " / " + h;
		}

		if (typeof onApplied === "function") {
			onApplied();
		}
	}

	if (img.complete === true && img.naturalWidth > 0) {
		apply();
		return;
	}

	img.addEventListener("load", apply);

	img.addEventListener("error", function onError() {
		img.removeEventListener("error", onError);

		if (typeof onApplied === "function") {
			onApplied();
		}
	});
}

function initDraggableLandingWindows() {
	if (isMobile === true) {
		return;
	}

	const stage = document.querySelector(".landing-windows");

	if (!stage) {
		return;
	}

	document.querySelectorAll(".landing-windows > .landing-window").forEach(function forEachWindow(win) {
		const title = win.querySelector(".landing-window-title");

		if (!title) {
			return;
		}

		let isMouseDown = false;
		let initialPointer;
		let initialWindowLocation;
		let moveLogRafId = null;

		function constrainWindowToViewport(nextLeft, nextTop) {
			const stageRect = stage.getBoundingClientRect();
			const ww = win.offsetWidth;
			const wh = win.offsetHeight;
			const pad = WINDOW_PADDING;
			let viewportLeft = stageRect.left + nextLeft;
			let viewportTop = stageRect.top + nextTop;

			viewportLeft = Math.min(Math.max(viewportLeft, pad), globalThis.innerWidth - pad - ww);
			viewportTop = Math.min(Math.max(viewportTop, pad), globalThis.innerHeight - pad - wh);

			return {
				left: viewportLeft - stageRect.left,
				top: viewportTop - stageRect.top
			};
		}

		function didMouseMove(e) {
			if (isMouseDown === false) {
				return;
			}

			const location = pointerLocation(e);
			const offset = {
				x: location.x - initialPointer.x,
				y: location.y - initialPointer.y
			};
			const nextLeft = initialWindowLocation.x + offset.x;
			const nextTop = initialWindowLocation.y + offset.y;
			const constrained = constrainWindowToViewport(nextLeft, nextTop);

			win.style.left = constrained.left + "px";
			win.style.top = constrained.top + "px";

			if (moveLogRafId === null) {
				moveLogRafId = requestAnimationFrame(function onMoveLogFrame() {
					moveLogRafId = null;
					logLandingWindowSnapshot(win, "move");
				});
			}
		}

		function didMouseUp() {
			isMouseDown = false;

			if (moveLogRafId !== null) {
				cancelAnimationFrame(moveLogRafId);
				moveLogRafId = null;
			}

			logAllLandingWindows("drag-end");

			document.removeEventListener("mousemove", didMouseMove);
			document.removeEventListener("mouseup", didMouseUp);
			document.removeEventListener("touchmove", didMouseMove);
			document.removeEventListener("touchend", didMouseUp);
		}

		function didMouseDown(e) {
			e.preventDefault();
			isMouseDown = true;
			initialPointer = pointerLocation(e);
			initialWindowLocation = { x: win.offsetLeft, y: win.offsetTop };

			document.addEventListener("mousemove", didMouseMove);
			document.addEventListener("mouseup", didMouseUp);
			document.addEventListener("touchmove", didMouseMove, { passive: false });
			document.addEventListener("touchend", didMouseUp);
		}

		title.addEventListener("mousedown", didMouseDown);
		title.addEventListener("touchstart", didMouseDown, { passive: false });
	});
}

function didClickLanguage(event) {
	document.cookie = "locale=" + event.currentTarget.hreflang + ";max-age=31536000;path=/";
}

function init() {
	for (const link of document.querySelectorAll(".landing-languages a")) {
		link.addEventListener("click", didClickLanguage);
	}

	const wallpaperCanvas = document.getElementById("landing-wallpaper-canvas");

	if (wallpaperCanvas) {
		mountLandingWallpaper(wallpaperCanvas);
	}

	if (isMobile === false) {
		initLandingWindowFocusOrder();
	}

	requestAnimationFrame(function onFrame() {
		applyScenesAspectFromPhoto(function afterScenesAspect() {
			if (isMobile === false) {
				materializeWindowPositions();
				initDraggableLandingWindows();
			}

			initLandingScrolls();
		});
	});

	initSlideshow();
}

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", init);
} else {
	init();
}
